import { defineComponent as _defineComponent } from 'vue'
import 'blueimp-file-upload';

import Vue, { nextTick, onBeforeUnmount, onMounted, reactive, ref, shallowRef, unref } from 'vue';

import { errutil, tablerender } from '#public/commons-global';
import { useGenerateIds } from '#public_ts/composables';

import type {
    FileUploadAddEvent,
    FileUploadApi,
    FileUploadBeforeSubmitEvent,
    FileUploadFileConstraints,
    FileUploadFinishEvent,
    FileUploadGroup,
    FileUploadIndexObject,
    FileUploadInitEvent,
    FileUploadResult,
    FileUploadStartEvent,
    FileUploadState,
    FileUploadStats,
} from './blueimp-file-upload';
import ProgressBar from './progress-bar.vue';
import type { ValidateFormApi } from './validate-form';
import ValidateForm from './validate-form.vue';

type NonReactiveGroup = JqueryFileUploadAddObject;

export default /*#__PURE__*/_defineComponent({
  __name: 'blueimp-file-upload',
  props: {
    accept: null,
    hideUpload: { type: Boolean },
    hideReset: { type: Boolean },
    constraints: null
  },
  emits: ["initialized", "adding", "starting", "beforesubmit", "finished"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'initialized', value: FileUploadInitEvent): void;
    (event: 'adding', value: FileUploadAddEvent): void;
    (event: 'starting', value: FileUploadStartEvent): void;
    (event: 'beforesubmit', value: FileUploadBeforeSubmitEvent): void;
    (event: 'finished', value: FileUploadFinishEvent): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    accept?: string;
    hideUpload?: boolean;
    hideReset?: boolean;
    constraints?: FileUploadFileConstraints;
}


//eslint-disable-next-line no-spaced-func


const generateIds = useGenerateIds();
const showButtons = ref(true);
const showProcessing = ref(false);
const formApi = shallowRef<ValidateFormApi | undefined>();
const file = shallowRef<HTMLInputElement | undefined>();
const form = shallowRef<HTMLFormElement | undefined>();
const buttonGroup = shallowRef<HTMLElement | undefined>();
let fileId = 0;
const fileState = reactive<FileUploadState>({
    files: [],
    fileGroups: [],
});
const uploadResult = ref<FileUploadResult>({
    log: '',
    stats: {},
});
const totalProgress = ref(0);
const totalProgressMessage = ref('');
const totalProgressShow = ref(false);
let nonReactiveIndex = 0;
const nonReactiveData = new Map<number, NonReactiveGroup>();
const findFileGroup = (data: unknown) => {
    const indexObject = data as FileUploadIndexObject;
    return fileState.fileGroups.find((g) => (g.contextIndex === indexObject.contextIndex));
};
let started = false;
const uploadApi: FileUploadApi = {
    start() {
        startClicked();
    },
    reset() {
        resetClicked();
    },
};

const haveGroup = function() {
    const _buttonGroup = unref(buttonGroup);
    return _buttonGroup && ($(_buttonGroup).children(':visible').length > 1);
};

expose(uploadApi);

function initialize() {
    const _formApi = unref(formApi);
    const _form = unref(_formApi?.formElement);
    const _file = unref(file);
    if(!_formApi || !_form || !_file) return;

    const _options: JQueryFileInputOptions & Record<string, unknown> = {
        singleFileUploads: false,
        sequentialUploads: true,
        ...props.constraints,
        downloadTemplateId: null,
        uploadTemplateId: null,
        filesContainer: $(),
        fileInput: $(_file),
        paramName: generateIds(),
        getNumberOfFiles() {
            return fileState.files.length;
        },
        add(e, data) {
            const index = nonReactiveIndex++;
            nonReactiveData.set(index, data);
            Object.assign(data, { contextIndex: index });

            const paramNames = Array.isArray(data.paramName) ? data.paramName : [];
            const files = data.files.map((f, i) => ({
                id: fileId++,
                file: f,
                error: null as string | null,
                group: {} as FileUploadGroup,
                metadata: {},
                paramName: paramNames[i] ?? '',
            }));
            const group = reactive({
                files,
                processing: true,
                progress: undefined,
                contextIndex: index,
                error: false,
                metadata: {},
            });
            files.forEach((f) => { f.group = group; });
            emit('adding', { group });
            fileState.files.push(...group.files);
            fileState.fileGroups.push(group);
            data.paramName = files.map((f) => f.paramName);
            showProcessing.value = true;
            Promise.resolve()
                .then(() => data.process())
                .catch(() => {
                    // validation error
                    const _files = data.files as File[] & { error?: boolean };
                    if(_files.error) {
                        group.error = true;
                        _files.forEach((f: File & { error?: string }, i) => {
                            const _f = files[i];
                            if(f.error && _f) {
                                _f.error = f.error;
                            }
                        });
                    }
                }).finally(() => {
                    group.processing = false;
                    showProcessing.value = false;
                });
        },
        change() {
            resetLog();
        },
        submit(e, data) {
            // prepare each group submit
            const group = findFileGroup(data);
            if(group) {
                let preventDefault = false;
                const submitEvent: FileUploadBeforeSubmitEvent = {
                    group,
                    formData: null,
                    preventDefault: () => {
                        preventDefault = true;
                    },
                };
                emit('beforesubmit', submitEvent);
                if(preventDefault) {
                    e.preventDefault();
                    return;
                }
                if(submitEvent.formData) {
                    data.formData = submitEvent.formData;
                }
            }
        },
        send(e, data) {
            // after each group submit
            const group = findFileGroup(data);
            if(group) {
                group.progress = (data.dataType?.substring(0, 6) === 'iframe') ? -1 : 0;
            }
        },
        done(e, data) {
            e.preventDefault();
            const result = data.result;
            if(errutil.ajaxerr(result)) {
                //something serious on server side
                resetClicked();
                return;
            }
            const _uploadResult = unref(uploadResult);
            _uploadResult.log += result.log;
            const stats = result.stats;
            for(const [key, value] of Object.entries(stats)) {
                if(typeof (value) != 'number') continue;
                const key0 = key as keyof FileUploadStats;
                Vue.set(_uploadResult.stats, key0, (_uploadResult.stats[key0] ?? 0) + value);
            }
            clearFiles();
        },
        fail(e, data) {
            e.preventDefault();
            resetClicked();
            if(data.errorThrown == 'abort') return;
            errutil.box(data.textStatus, data.errorThrown);
        },
        start(e) {
            e.preventDefault();
            totalProgress.value = 0;
            totalProgressShow.value = true;
            showButtons.value = false;
            started = true;
            resetLog();
        },
        stop(e) {
            e.preventDefault();
            totalProgress.value = 0;
            totalProgressShow.value = false;
            showButtons.value = true;
            started = false;
            const _uploadResult = unref(uploadResult);
            emit('finished', { result: _uploadResult });
            resetLog();
        },
        progress(e, data) {
            e.preventDefault();
            const group = findFileGroup(data);
            if(group) {
                const progress = data.total ? Math.floor(((data.loaded ?? 0) / data.total) * 100) : 0;
                group.progress = progress;
            }
        },
        progressall(e, data) {
            e.preventDefault();
            const progress = data.total ? Math.floor(((data.loaded ?? 0) / data.total) * 100) : 0;
            totalProgress.value = progress;

            const _formatTime = (seconds: number) => {
                /* eslint-disable prefer-template */
                const date = new Date(seconds * 1000);
                const days0 = Math.floor(seconds / 86400);
                const days = days0 ? days0 + 'd ' : '';
                return days + ('0' + date.getUTCHours()).slice(-2)
                    + ':' + ('0' + date.getUTCMinutes()).slice(-2)
                    + ':' + ('0' + date.getUTCSeconds()).slice(-2);
                /* eslint-enable prefer-template */
            };
            const message = `${tablerender.renderBytes(data.bitrate)}/s | \
${_formatTime(data.bitrate ? ((((data.total ?? 0) - (data.loaded ?? 0)) * 8) / data.bitrate) : 0)} | \
${progress.toFixed(2)}% | \
${tablerender.renderBytes(data.loaded ?? 0)} / ${tablerender.renderBytes(data.total ?? 0)}`;
            totalProgressMessage.value = message;
        },
        processstart(e) {
            e.preventDefault();
            showProcessing.value = true;
        },
        processstop(e) {
            e.preventDefault();
            showProcessing.value = false;
        },
    };
    $(_form).fileupload(_options);
    form.value = _form;
    emit('initialized', { state: fileState, formApi: _formApi, uploadApi });
}

const nonReentrant = function(fn: () => void) {
    let running = false;
    return function() {
        if(running) return;
        running = true;
        try {
            fn();
        } finally {
            running = false;
        }
    };
};

const _startClicked = nonReentrant(() => {
    const _formApi = unref(formApi);
    if(!_formApi) return;
    if(started) return;
    if(!_formApi.validateForm()) return;

    if(!fileState.fileGroups.length) {
        errutil.box('Nessun file selezionato per importazione.');
        return;
    }

    const haveError = fileState.fileGroups.some((data) => data?.error);
    if(haveError) {
        errutil.box('File selezionati non validi.');
        return;
    }

    let preventDefault = false;
    const startEvent = {
        state: fileState,
        formApi: _formApi,
        preventDefault: () => {
            preventDefault = true;
        },
    };
    emit('starting', startEvent);
    if(preventDefault) return;

    started = true;
    for(const data of nonReactiveData.values()) {
        data?.submit();
    }
});

function startClicked() {
    _startClicked();
}

const _resetClicked = nonReentrant(() => {
    if(started) return;
    for(const data of nonReactiveData.values()) {
        data?.abort();
    }
    clearFiles();
    resetLog();
});

function resetClicked() {
    _resetClicked();
}

function clearFiles() {
    nonReactiveData.clear();
    fileState.fileGroups = [];
    fileState.files = [];
}

function resetLog() {
    uploadResult.value = {
        log: '',
        stats: {},
    };
}

onMounted(() => {
    nextTick(() => {
        initialize();
    });
});
onBeforeUnmount(() => {
    const _form = unref(form);
    if(!_form) return;
    $(_form).fileupload('destroy');
});

return { __sfc: true,props, emit, generateIds, showButtons, showProcessing, formApi, file, form, buttonGroup, fileId, fileState, uploadResult, totalProgress, totalProgressMessage, totalProgressShow, nonReactiveIndex, nonReactiveData, findFileGroup, started, uploadApi, haveGroup, initialize, nonReentrant, _startClicked, startClicked, _resetClicked, resetClicked, clearFiles, resetLog, ProgressBar, ValidateForm }
}

})